import { FC, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Typography } from '@mui/material'

import { GRTable, GRTableColumn, SortOrder } from '../../../../components/GRTable/GRTable'
import { clickableProps } from '../../helpers/interactive'
import { linkTypographyBoldSx, linkTypographySx } from '../../helpers/styles'
import { isUnknownGenre } from '../../helpers/tagColumns'
import { useControlledColumns } from '../../hooks/useControlledColumns'
import { useTagGamesListing } from '../../hooks/useTagGamesListing'
import { useUAInsightsNavigation } from '../../hooks/useUAInsightsNavigation'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import { TagGame } from '../../types'
import { SectionCard } from '../SectionCard/SectionCard'
import { TableSkeleton } from '../TableSkeleton/TableSkeleton'
import { UAInsightsDialog } from '../UAInsightsDialog/UAInsightsDialog'

const gameRowId = (row: TagGame) => row.id

export const TagGamesDialog: FC = () => {
  const { t } = useTranslation('ua-insights')
  const { params, close, canGoBack, goBack, openGame, openGenreTags, openTagForGame } = useUAInsightsNavigation()
  const { tagLabel, genreLabel } = useUATaxonomy()
  const open = params.dialog === 'tag-games'
  const tagId = open ? params.tagId : null
  const { data, isLoading, isError, refetch } = useTagGamesListing(tagId)

  const handleGameClick = useCallback((gameId: string) => openGame(gameId), [openGame])
  const handleGenreClick = useCallback((genreId: string) => openGenreTags(genreId), [openGenreTags])
  const handleCreativesClick = useCallback(
    (gameId: string) => {
      if (tagId) openTagForGame(tagId, gameId)
    },
    [openTagForGame, tagId]
  )

  const defaultColumns: GRTableColumn<TagGame, undefined>[] = useMemo(
    () => [
      {
        labelAccessor: t('col_game_name'),
        accessor: ({ row }) => (
          <Typography variant="body2" sx={linkTypographyBoldSx} {...clickableProps(() => handleGameClick(row.id))}>
            {row.name}
          </Typography>
        ),
        sortAccessor: 'name',
        sortable: true,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { fontWeight: 'bold' } },
      },
      {
        labelAccessor: t('col_genre'),
        accessor: ({ row }) =>
          isUnknownGenre(row.genre) ? (
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              {t('common:not_available_shorthand')}
            </Typography>
          ) : (
            <Typography variant="body2" sx={linkTypographySx} {...clickableProps(() => handleGenreClick(row.genre.id))}>
              {genreLabel(row.genre)}
            </Typography>
          ),
        sortAccessor: ({ row }) => (isUnknownGenre(row.genre) ? '' : genreLabel(row.genre)),
        sortable: true,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { fontWeight: 'bold' } },
      },
      {
        labelAccessor: t('col_creatives'),
        accessor: ({ row }) => (
          <Typography variant="body2" sx={linkTypographySx} {...clickableProps(() => handleCreativesClick(row.id))}>
            {row.creativesCount}
          </Typography>
        ),
        sortAccessor: 'creativesCount',
        sortable: true,
        sortOrder: SortOrder.DESC,
        width: 120,
        cellProps: { align: 'center' },
        headerCellProps: { align: 'center', sx: { fontWeight: 'bold' } },
      },
    ],
    [t, genreLabel, handleGameClick, handleGenreClick, handleCreativesClick]
  )

  const [columns, setColumns] = useControlledColumns(defaultColumns)

  const title = data ? t('games_with_tag', { tag: tagLabel(data.tag.tag) }) : t('tag_details')

  return (
    <UAInsightsDialog
      open={open}
      onClose={close}
      title={title}
      canGoBack={canGoBack}
      onGoBack={goBack}
      showNetworkSelector
      hasData={!!data}
      isLoading={isLoading}
      isError={isError}
      onRetry={refetch}
      loadingPlaceholder={<TableSkeleton rows={10} columns={5} />}
      notFoundFallback={
        <Typography variant="body2" color="text.secondary">
          {t('tag_details_not_found')}
        </Typography>
      }
    >
      {data && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <SectionCard title={t('games_with_tag_section', { count: data.games.length })} disableBodyPadding>
            <GRTable rows={data.games} columns={columns} onColumnsUpdated={setColumns} striped hoverable rowIdKey={gameRowId} noRowsLabel={t('no_games')} />
          </SectionCard>
        </Box>
      )}
    </UAInsightsDialog>
  )
}
